import { useCallback, useEffect, useRef } from 'react';
import type { GameState } from '../../lib/types';

/** Tracce usate dal maxischermo (`public/audio`). */
export const SCREEN_STAGE_AUDIO = {
  /** Beep di prova subito dopo il consenso (maxischermo). */
  consentDemo: '/audio/00-consent-demo.mp3',
  /** Lobby, IDLE (gara non attiva), countdown post-generazione → voto. */
  standby: '/audio/07-song.mp3',
  timer: '/audio/timer-3.mp3',
  suspense: '/audio/05-suspance-2.MP3',
  generated: '/audio/02-denerated.MP3',
  voting: '/audio/06-voting.MP3',
  winner: '/audio/03-winner.MP3',
} as const;

/** Prefisso console: filtra con `[maxi-audio]`. */
export const MAXI_AUDIO_LOG_TAG = '[maxi-audio]';

function maxiLog(step: string, payload?: Record<string, unknown>) {
  if (payload && Object.keys(payload).length > 0) {
    console.info(MAXI_AUDIO_LOG_TAG, step, payload);
  } else {
    console.info(MAXI_AUDIO_LOG_TAG, step);
  }
}

function maxiMediaSnapshot(el: HTMLAudioElement) {
  return {
    src: el.currentSrc || el.src || '',
    paused: el.paused,
    muted: el.muted,
    volume: el.volume,
    loop: el.loop,
    readyState: el.readyState,
    networkState: el.networkState,
    error: el.error ? { code: el.error.code, message: el.error.message } : null,
  };
}

function playWithLog(el: HTMLAudioElement, ruolo: string, pathImpostato: string): void {
  void el.play().then(
    () => {
      maxiLog(`play OK (${ruolo})`, { path: pathImpostato, ...maxiMediaSnapshot(el) });
    },
    (err: unknown) => {
      console.error(MAXI_AUDIO_LOG_TAG, `play FALLITO (${ruolo})`, {
        path: pathImpostato,
        errore: err instanceof Error ? err.message : String(err),
        nome: err instanceof DOMException ? err.name : undefined,
        ...maxiMediaSnapshot(el),
      });
    },
  );
}

/** Chrome rifiuta la promise se `pause()` viene chiamato nello stesso tick (warm voluto). */
function isWarmPlayInterruptedByPause(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err);
  return msg.includes('interrupted by a call to pause');
}

/** Precarica tutto tranne il demo consenso (ha un nodo dedicato). */
const PRELOAD_URLS: readonly string[] = (
  Object.values(SCREEN_STAGE_AUDIO) as readonly string[]
).filter((url) => url !== SCREEN_STAGE_AUDIO.consentDemo);

/**
 * Chiamare solo dentro un gesto utente (`click`). Suona un doppio tono sintetico
 * (non dipende dal caricamento del file MP3).
 */
function playConsentChimeWebAudio(existing: AudioContext | null): AudioContext | null {
  if (typeof window === 'undefined') return existing;
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return existing;

  let ctx = existing;
  if (!ctx || ctx.state === 'closed') {
    ctx = new Ctor();
  }

  try {
    if (ctx.state === 'suspended') {
      void ctx.resume();
    }
  } catch (e) {
    console.warn(MAXI_AUDIO_LOG_TAG, 'AudioContext.resume() eccezione', e);
  }

  const t0 = ctx.currentTime + 0.02;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(587.33, t0);
  osc.frequency.linearRampToValueAtTime(880, t0 + 0.16);
  gain.gain.setValueAtTime(0, t0);
  gain.gain.linearRampToValueAtTime(0.35, t0 + 0.02);
  gain.gain.linearRampToValueAtTime(0.26, t0 + 0.12);
  gain.gain.linearRampToValueAtTime(0, t0 + 0.42);
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(t0);
  osc.stop(t0 + 0.44);

  maxiLog('Web Audio: chime consenso schedulato', {
    ctxState: ctx.state,
    t0,
  });

  return ctx;
}

function configureMediaEl(el: HTMLAudioElement) {
  el.setAttribute('playsinline', '');
  el.setAttribute('webkit-playsinline', '');
  el.preload = 'auto';
}

function sameAsset(absoluteSrc: string, path: string): boolean {
  if (!absoluteSrc) return false;
  try {
    const u = new URL(absoluteSrc, window.location.origin);
    return u.pathname.toLowerCase().endsWith(path.toLowerCase());
  } catch {
    return absoluteSrc.toLowerCase().endsWith(path.toLowerCase());
  }
}

function stopLoop(el: HTMLAudioElement | null) {
  if (!el) return;
  el.pause();
  el.loop = false;
}

function targetLoopSrc(s: GameState | null): string | null {
  if (!s) return null;
  if (s.status === 'IDLE') return SCREEN_STAGE_AUDIO.standby;
  if (s.status === 'WAITING_FOR_PLAYERS') return SCREEN_STAGE_AUDIO.standby;
  if (s.status === 'GENERATING' && s.generation_finished_at) {
    return SCREEN_STAGE_AUDIO.standby;
  }
  if (s.status === 'WRITING') return SCREEN_STAGE_AUDIO.timer;
  if (s.status === 'GENERATING' && !s.generation_finished_at) {
    return SCREEN_STAGE_AUDIO.suspense;
  }
  if (s.status === 'VOTING') return SCREEN_STAGE_AUDIO.voting;
  return null;
}

export interface UseScreenStageAudioOptions {
  enabled: boolean;
  /** Dopo il clic «Consento» sulla barra regia (gesto utente reale in questa pagina). */
  audioPrimed: boolean;
}

export interface UseScreenStageAudioResult {
  /**
   * Da chiamare **solo** nel gesto `onClick` del pulsante consenso: avvia subito
   * il loop della fase corrente così il browser associa il play al tocco.
   */
  primeAudioInUserGesture: () => void;
  /**
   * Stessi `onClick`: esegue stinger rimasti in coda (generazione / vincitore) se
   * l’evento è avvenuto prima del consenso.
   */
  flushPendingStingersInUserGesture: () => void;
  /** Stessi `onClick`: breve audio di demo per verificare che l’uscita audio funzioni. */
  playConsentDemoInUserGesture: () => void;
}

/**
 * Audio di regia per `/screen`: precarica le tracce, loop per fase, stinger.
 * Senza `audioPrimed` non viene chiamato `play()` (evita errori e rumore in sala).
 */
export function useScreenStageAudio(
  state: GameState | null,
  options: UseScreenStageAudioOptions,
): UseScreenStageAudioResult {
  const { enabled, audioPrimed } = options;
  const loopRef = useRef<HTMLAudioElement | null>(null);
  const stingRef = useRef<HTMLAudioElement | null>(null);
  const stateRef = useRef<GameState | null>(null);
  stateRef.current = state;

  const prevStatusRef = useRef<string | null>(null);
  const prevGenFinRef = useRef<string | null>(null);
  const bootstrappedRef = useRef(false);
  const sessionBootstrapRef = useRef<string | null>(null);
  const playedGeneratedKeyRef = useRef<string | null>(null);
  const playedWinnerRoundRef = useRef<number | null>(null);
  /** Stinger saltato perché non c’era ancora consenso: eseguito al primo consenso utile. */
  const pendingWinnerRoundRef = useRef<number | null>(null);
  const pendingGenRef = useRef<{ round: number; genFin: string } | null>(null);
  /** Stinger avviato dal flush consenso: evita loop nello stesso tick dell’effect. */
  const deferPhaseLoopAfterFlushStingerRef = useRef(false);
  const consentDemoElRef = useRef<HTMLAudioElement | null>(null);
  const consentWebAudioCtxRef = useRef<AudioContext | null>(null);

  const startLoop = (el: HTMLAudioElement, path: string) => {
    if (sameAsset(el.src, path) && el.loop && !el.paused) {
      maxiLog('startLoop: già in riproduzione, skip', { path, ...maxiMediaSnapshot(el) });
      return;
    }
    maxiLog('startLoop: set src + play', { path, prima: maxiMediaSnapshot(el) });
    el.pause();
    el.muted = false;
    el.src = path;
    el.loop = true;
    el.volume = path === SCREEN_STAGE_AUDIO.standby ? 0.58 : 0.72;
    playWithLog(el, 'loop-fase', path);
  };

  const startLoopRef = useRef(startLoop);
  startLoopRef.current = startLoop;

  const playStinger = (el: HTMLAudioElement, path: string) => {
    maxiLog('playStinger: uno-shot', { path, prima: maxiMediaSnapshot(el) });
    el.pause();
    el.loop = false;
    el.muted = false;
    el.src = path;
    el.volume = 0.88;
    playWithLog(el, 'stinger', path);
  };

  /** Sblocco policy browser: play muto nello stesso stack del `click`. */
  const warmElementInUserGesture = (el: HTMLAudioElement, src: string, label: string) => {
    maxiLog(`warm (${label}): inizio`, { warmSrc: src, prima: maxiMediaSnapshot(el) });
    try {
      el.pause();
      el.muted = true;
      el.volume = 0;
      el.loop = false;
      el.src = src;
      void el.play().then(
        () => maxiLog(`warm (${label}): play() risolto`, maxiMediaSnapshot(el)),
        (err: unknown) => {
          if (isWarmPlayInterruptedByPause(err)) {
            maxiLog(`warm (${label}): play() annullato da pause() nello stesso tick (ok)`, maxiMediaSnapshot(el));
          } else {
            console.error(MAXI_AUDIO_LOG_TAG, `warm (${label}): play() rifiutato`, {
              errore: err instanceof Error ? err.message : String(err),
              ...maxiMediaSnapshot(el),
            });
          }
        },
      );
      el.pause();
      el.currentTime = 0;
    } catch (e) {
      console.error(MAXI_AUDIO_LOG_TAG, `warm (${label}): eccezione sync`, e, maxiMediaSnapshot(el));
    }
    el.muted = false;
    maxiLog(`warm (${label}): fine stack`, { ...maxiMediaSnapshot(el) });
  };

  const primeAudioInUserGesture = useCallback(() => {
    maxiLog('prime (click consenso): inizio', {
      want: targetLoopSrc(stateRef.current),
      status: stateRef.current?.status,
      round: stateRef.current?.round,
    });
    const loopEl = loopRef.current;
    const stingEl = stingRef.current;
    if (!loopEl) {
      maxiLog('prime: loopRef assente, esco');
      return;
    }

    const want = targetLoopSrc(stateRef.current);
    const warmupSrc = SCREEN_STAGE_AUDIO.timer;

    warmElementInUserGesture(loopEl, warmupSrc, 'loop');
    if (stingEl) {
      warmElementInUserGesture(stingEl, warmupSrc, 'sting');
      stingEl.pause();
      stingEl.removeAttribute('src');
      stingEl.load();
      maxiLog('prime: sting reset src dopo warm');
    }

    if (want) {
      maxiLog('prime: avvio loop fase richiesta', { want });
      startLoop(loopEl, want);
    } else {
      maxiLog('prime: nessun loop per questa fase, loop silenziato');
      loopEl.pause();
      loopEl.removeAttribute('src');
      loopEl.load();
    }
    maxiLog('prime (click consenso): fine', { want, ...maxiMediaSnapshot(loopEl) });
  }, []);

  const flushPendingStingersInUserGesture = useCallback(() => {
    maxiLog('flush stinger (click consenso): inizio', {
      pendingWinner: pendingWinnerRoundRef.current,
      pendingGen: pendingGenRef.current,
    });
    const stingEl = stingRef.current;
    const loopEl = loopRef.current;
    const st = stateRef.current;
    if (!stingEl || !loopEl || !st) {
      maxiLog('flush: sting/loop/state assente, esco', {
        stingEl: Boolean(stingEl),
        loopEl: Boolean(loopEl),
        st: Boolean(st),
      });
      return;
    }
    const status = st.status;
    const genFin = st.generation_finished_at ?? null;
    const round = st.round;

    if (pendingWinnerRoundRef.current !== null) {
      const pr = pendingWinnerRoundRef.current;
      if (status === 'ENDED' && round === pr) {
        pendingWinnerRoundRef.current = null;
        maxiLog('flush: eseguo winner pendente', { round: pr, path: SCREEN_STAGE_AUDIO.winner });
        stopLoop(loopEl);
        deferPhaseLoopAfterFlushStingerRef.current = true;
        playStinger(stingEl, SCREEN_STAGE_AUDIO.winner);
        playedWinnerRoundRef.current = round;
      } else {
        maxiLog('flush: winner pendente ma condizioni no match', {
          pr,
          status,
          round,
        });
      }
    }

    if (pendingGenRef.current) {
      const pg = pendingGenRef.current;
      if (status === 'GENERATING' && genFin === pg.genFin && round === pg.round) {
        pendingGenRef.current = null;
        const key = `${round}:${genFin}`;
        maxiLog('flush: eseguo generated pendente', { key, path: SCREEN_STAGE_AUDIO.generated });
        stopLoop(loopEl);
        deferPhaseLoopAfterFlushStingerRef.current = true;
        playStinger(stingEl, SCREEN_STAGE_AUDIO.generated);
        playedGeneratedKeyRef.current = key;
      } else {
        maxiLog('flush: gen pendente ma condizioni no match', {
          pg,
          status,
          genFin,
          round,
        });
      }
    }
    maxiLog('flush stinger (click consenso): fine');
  }, []);

  const playConsentDemoInUserGesture = useCallback(() => {
    maxiLog('consent demo: Web Audio chime + MP3');
    consentWebAudioCtxRef.current = playConsentChimeWebAudio(consentWebAudioCtxRef.current);

    const mp3 = consentDemoElRef.current;
    if (mp3) {
      try {
        mp3.pause();
        mp3.currentTime = 0;
        mp3.muted = false;
        mp3.volume = 1;
        maxiLog('consent demo MP3: prima play', maxiMediaSnapshot(mp3));
        playWithLog(mp3, 'consent-demo-mp3', SCREEN_STAGE_AUDIO.consentDemo);
      } catch (e) {
        console.error(MAXI_AUDIO_LOG_TAG, 'consent demo MP3: eccezione sync', e, maxiMediaSnapshot(mp3));
      }
    } else {
      maxiLog('consent demo MP3: ref null (nodo non montato?)');
    }
  }, []);

  useEffect(() => {
    const preload = PRELOAD_URLS.map((src) => {
      const a = new Audio();
      configureMediaEl(a);
      a.src = src;
      a.load();
      return a;
    });
    const loop = new Audio();
    const sting = new Audio();
    configureMediaEl(loop);
    configureMediaEl(sting);
    loopRef.current = loop;
    stingRef.current = sting;

    const consentMp3 = new Audio();
    configureMediaEl(consentMp3);
    consentMp3.src = SCREEN_STAGE_AUDIO.consentDemo;
    consentMp3.preload = 'auto';
    consentMp3.load();
    consentDemoElRef.current = consentMp3;

    const onLoopError = () => {
      console.error(MAXI_AUDIO_LOG_TAG, 'evento "error" su loop', maxiMediaSnapshot(loop));
    };
    const onStingError = () => {
      console.error(MAXI_AUDIO_LOG_TAG, 'evento "error" su sting', maxiMediaSnapshot(sting));
    };
    const onConsentMp3Error = () => {
      console.error(MAXI_AUDIO_LOG_TAG, 'evento "error" su consentMp3', maxiMediaSnapshot(consentMp3));
    };
    loop.addEventListener('error', onLoopError);
    sting.addEventListener('error', onStingError);
    consentMp3.addEventListener('error', onConsentMp3Error);

    return () => {
      consentWebAudioCtxRef.current?.close();
      consentWebAudioCtxRef.current = null;
      loop.removeEventListener('error', onLoopError);
      sting.removeEventListener('error', onStingError);
      consentMp3.removeEventListener('error', onConsentMp3Error);
      consentMp3.pause();
      consentMp3.removeAttribute('src');
      consentMp3.load();
      consentDemoElRef.current = null;
      for (const a of preload) {
        a.pause();
        a.removeAttribute('src');
        a.load();
      }
      loop.pause();
      loop.removeAttribute('src');
      loop.load();
      sting.pause();
      sting.removeAttribute('src');
      sting.load();
      loopRef.current = null;
      stingRef.current = null;
      bootstrappedRef.current = false;
      sessionBootstrapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const loopEl = loopRef.current;
    const stingEl = stingRef.current;
    if (!loopEl || !stingEl) return;

    if (!enabled) {
      stopLoop(loopEl);
      stingEl.pause();
      return;
    }

    const sid = state?.session_id ?? null;
    if (sessionBootstrapRef.current !== sid) {
      sessionBootstrapRef.current = sid;
      bootstrappedRef.current = false;
      prevStatusRef.current = null;
      prevGenFinRef.current = null;
      playedGeneratedKeyRef.current = null;
      playedWinnerRoundRef.current = null;
      pendingWinnerRoundRef.current = null;
      pendingGenRef.current = null;
      deferPhaseLoopAfterFlushStingerRef.current = false;
    }

    const status = state?.status ?? null;
    const genFin = state?.generation_finished_at ?? null;
    const round = state?.round ?? 0;

    const canPlay = enabled && audioPrimed;
    let suppressPhaseLoopAfterStinger = false;
    const wantLoopEarly = targetLoopSrc(state);
    maxiLog('effect fase audio (tick)', {
      enabled,
      audioPrimed,
      canPlay,
      status,
      genFin,
      round,
      wantLoop: wantLoopEarly,
      bootstrap: bootstrappedRef.current,
      prevStatus: prevStatusRef.current,
      prevGenFin: prevGenFinRef.current,
      pendingWinner: pendingWinnerRoundRef.current,
      pendingGen: pendingGenRef.current,
    });

    if (canPlay && pendingWinnerRoundRef.current !== null) {
      const pr = pendingWinnerRoundRef.current;
      if (status === 'ENDED' && round === pr) {
        pendingWinnerRoundRef.current = null;
        stopLoop(loopEl);
        suppressPhaseLoopAfterStinger = true;
        playStinger(stingEl, SCREEN_STAGE_AUDIO.winner);
        playedWinnerRoundRef.current = round;
      }
    }

    if (canPlay && pendingGenRef.current) {
      const pg = pendingGenRef.current;
      if (status === 'GENERATING' && genFin === pg.genFin && round === pg.round) {
        pendingGenRef.current = null;
        const key = `${round}:${genFin}`;
        stopLoop(loopEl);
        suppressPhaseLoopAfterStinger = true;
        playStinger(stingEl, SCREEN_STAGE_AUDIO.generated);
        playedGeneratedKeyRef.current = key;
      }
    }

    if (!audioPrimed) {
      maxiLog('effect: audio non ancora acconsentito → stop loop/sting', { status });
      stopLoop(loopEl);
      stingEl.pause();
    }

    if (!bootstrappedRef.current) {
      bootstrappedRef.current = true;
      prevStatusRef.current = status;
      prevGenFinRef.current = genFin;
      maxiLog('effect: primo bootstrap sessione', { canPlay, status, wantLoop: targetLoopSrc(state) });
      if (canPlay) {
        const loopSrc = targetLoopSrc(state);
        if (loopSrc) {
          maxiLog('effect bootstrap: startLoop', { loopSrc });
          startLoop(loopEl, loopSrc);
        } else {
          maxiLog('effect bootstrap: nessun loop per fase');
          stopLoop(loopEl);
        }
      } else {
        maxiLog('effect bootstrap: canPlay false, nessun avvio');
      }
      return;
    }

    const prevStatus = prevStatusRef.current;
    const prevGenFin = prevGenFinRef.current;

    if (prevStatus === 'VOTING' && status === 'ENDED') {
      if (playedWinnerRoundRef.current !== round) {
        maxiLog('edge VOTING→ENDED: winner stinger', { canPlay, round });
        stopLoop(loopEl);
        if (canPlay) {
          suppressPhaseLoopAfterStinger = true;
          playStinger(stingEl, SCREEN_STAGE_AUDIO.winner);
          playedWinnerRoundRef.current = round;
        } else {
          pendingWinnerRoundRef.current = round;
          maxiLog('edge VOTING→ENDED: winner in coda (no consenso)', { round });
        }
      }
    }

    if (status === 'GENERATING' && genFin && genFin !== prevGenFin) {
      const key = `${round}:${genFin}`;
      if (playedGeneratedKeyRef.current !== key) {
        maxiLog('edge GENERATING genFin cambiato: stinger generated', { key, canPlay });
        stopLoop(loopEl);
        if (canPlay) {
          suppressPhaseLoopAfterStinger = true;
          playStinger(stingEl, SCREEN_STAGE_AUDIO.generated);
          playedGeneratedKeyRef.current = key;
        } else {
          pendingGenRef.current = { round, genFin };
          maxiLog('edge GENERATING: generated in coda (no consenso)', { round, genFin });
        }
      }
    }

    prevStatusRef.current = status;
    prevGenFinRef.current = genFin;

    const deferFromFlushStinger = deferPhaseLoopAfterFlushStingerRef.current;
    if (deferFromFlushStinger) deferPhaseLoopAfterFlushStingerRef.current = false;
    const suppressAnyPhaseLoop =
      suppressPhaseLoopAfterStinger || deferFromFlushStinger;

    const wantLoop = targetLoopSrc(state);
    if (canPlay && wantLoop && !suppressAnyPhaseLoop) {
      maxiLog('effect: mantengo/aggiorno loop fase', { wantLoop, status });
      startLoop(loopEl, wantLoop);
    } else {
      if (!canPlay && wantLoop) {
        maxiLog('effect: loop voluto ma canPlay false → stop', { wantLoop, audioPrimed, enabled });
      } else if (canPlay && !wantLoop) {
        maxiLog('effect: nessun loop per questa fase → stop', { status });
      } else if (suppressAnyPhaseLoop && wantLoop) {
        maxiLog('effect: loop fase rinviato (stinger nello stesso tick)', { wantLoop, status });
      }
      stopLoop(loopEl);
    }
  }, [
    enabled,
    audioPrimed,
    state?.status,
    state?.generation_finished_at,
    state?.round,
    state?.session_id,
  ]);

  useEffect(() => {
    const stingEl = stingRef.current;
    if (!stingEl) return;

    const onStingEnded = () => {
      if (!enabled || !audioPrimed) return;
      const st = stateRef.current;
      const loopEl = loopRef.current;
      if (!st || !loopEl) return;
      const src = stingEl.currentSrc || stingEl.src;
      if (!src) return;
      if (
        sameAsset(src, SCREEN_STAGE_AUDIO.generated) &&
        st.status === 'GENERATING' &&
        st.generation_finished_at
      ) {
        maxiLog('sting ended → standby', { dopo: 'generated' });
        startLoopRef.current(loopEl, SCREEN_STAGE_AUDIO.standby);
        return;
      }
      if (sameAsset(src, SCREEN_STAGE_AUDIO.winner) && st.status === 'ENDED') {
        maxiLog('sting ended → standby', { dopo: 'winner' });
        startLoopRef.current(loopEl, SCREEN_STAGE_AUDIO.standby);
      }
    };

    stingEl.addEventListener('ended', onStingEnded);
    return () => stingEl.removeEventListener('ended', onStingEnded);
  }, [enabled, audioPrimed]);

  return { primeAudioInUserGesture, flushPendingStingersInUserGesture, playConsentDemoInUserGesture };
}
